import {translate} from "react-i18nify";

import * as actionTypes from "../constants/actionTypes";
import * as componentTypes from "../constants/componentTypes";
import * as config from "../constants/config";
import * as handlers from "../constants/handlers";
import * as importData from "../constants/importData";
import * as methods from "../constants/methods";
import * as orderStatus from "../constants/orderStatus";
import * as settings from "../constants/settings";
import * as ticketingActions from "../actions/ticketingActions";
import * as partialUpdatesActions from "../actions/partialUpdatesActions";
import * as eventResponseHelper from "../helpers/eventResponseHelper";
import * as modelHelper from "../helpers/modelHelper";
import * as messageHelper from "../helpers/messageHelper";

export const processWebsocketEventResponse = (response, state) => {
    if (response.event === methods.SERVER_EVENT_SHOW_MESSAGE) {
        return [
            {
                type: actionTypes.SAVE_FLASH_MESSAGE,
                payload: {
                    flashMessage: modelHelper.getCustomServerFlashMessage(response.data)
                }
            }
        ];
    }

    if (response.event === methods.SERVER_EVENT_DATABASE_IMPORT_ITEM) {
        return [
            {
                type: actionTypes.SAVE_EXCEL_FILE_ITEM,
                payload: response.data
            }
        ];
    }

    if (response.event === methods.SERVER_EVENT_DATABASE_IMPORT_STEP) {
        return [
            {
                type: actionTypes.SAVE_EXCEL_FILE_STEP,
                payload: response.data
            }
        ];
    }

    if (!state.login.loggedIn) {
        switch (response.event) {
            case methods.SERVER_EVENT_DATABASE_UPDATE_IN_PROGRESS:
                return [
                    {
                        type: actionTypes.DATABASE_UPDATE_IN_PROGRESS
                    }
                ];
            case methods.SERVER_EVENT_DATABASE_UPDATE_FINISHED:
                return [
                    {
                        type: actionTypes.INCREASE_NUMBER_OF_DATABASE_UPDATES
                    },
                    {
                        type: actionTypes.RESET_SERVER_CONNECTION
                    }
                ];
            default:
                return [
                    {
                        type: actionTypes.UNKNOWN_ACTION
                    }
                ];
        }
    }

    switch (response.event) {
        case methods.SERVER_EVENT_SERVERS_UPDATED:
            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_AVAILABLE_SERVERS,
                        params: {
                            compatibleOnly: true,
                            activeOnly: true,
                            orderingOnly: true
                        }
                    },
                    method: handlers.GET_COMPATIBLE_SERVERS,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_AVAILABLE_SERVERS,
                        params: {
                            compatibleOnly: false,
                            activeOnly: false
                        }
                    },
                    method: handlers.GET_SERVER_MANAGEMENT_LIST,
                    tabId: 0
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_DATABASES_LIST
                    },
                    method: handlers.GET_DATABASES_LIST,
                    tabId: 0
                }
            ];
        case methods.SERVER_EVENT_ORDERS_LIST_UPDATED:
            let toDispenseTab = state.navigation.tabList.find((tab) => tab.type === componentTypes.TO_DISPENSE_TAB);

            let ordersUpdateActions = [
                {
                    type: actionTypes.ORDERS_HISTORY_LIST_UPDATE,
                    payload: {
                        tabId: toDispenseTab ? toDispenseTab.id : 0
                    }
                },
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_TO_DISPENSE_GET_ORDERS,
                        params: {
                            offset: 0,
                            rowCount: config.MAX_ORDERS_LIMIT,
                            statusFilter: orderStatus.TO_DISPENSE_ORDER_STATUS_LIST
                        }
                    },
                    method: handlers.TO_DISPENSE_GET_ORDERS_WITH_NOTIFICATION,
                    tabId: toDispenseTab ? toDispenseTab.id : 0
                }
            ];

            if (state.toDispense.activeOrder !== null) {
                ordersUpdateActions.push({
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_ORDER_DETAIL,
                        params: {
                            id: state.toDispense.activeOrder.id
                        }
                    },
                    method: handlers.TO_DISPENSE_GET_DETAIL,
                    tabId: 0
                });
            }

            return ordersUpdateActions;
        case methods.SERVER_EVENT_ORDER_STATE_CHANGED:
            if (!eventResponseHelper.isOrderStateValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            return [
                {
                    type: actionTypes.SAVE_ORDER_STATUS,
                    payload: {
                        orderIdentifier: response.data.identifier,
                        status: response.data.status,
                        updated: response.data.timestamp
                    }
                },
                {
                    type: actionTypes.ORDERS_HISTORY_LIST_UPDATE,
                    payload: {
                        tabId: 0
                    }
                }
            ];
        case methods.SERVER_EVENT_PROGRESS:
            if (!eventResponseHelper.isProgressStateValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }
            switch (response.data.type) {
                case importData.PROGRESS_IMPORT_CUSTOM_DATA:
                    return [
                        {
                            type: actionTypes.CUSTOM_DATA_SAVE_PROGRESS,
                            payload: {
                                progress: response.data.progress,
                                task: response.data.taskName
                            }
                        }
                    ];
                default:
                    return [];
            }
        case methods.SERVER_EVENT_LAST_DISPENSED_UPDATED:
            if (!eventResponseHelper.isLastDispensedFormulasUpdatedValid(response)) {
                return [messageHelper.getInvalidFormatMessage(response)];
            }

            return [
                {
                    type: actionTypes.SAVE_LAST_DISPENSED_FORMULA_LIST,
                    payload: {
                        lastDispensedList: response.data
                    }
                }
            ];
        case methods.SERVER_EVENT_FILE_TRANSFER_STARTED:
            return [
                {
                    type: actionTypes.FILE_TRANSFER_STARTED
                }
            ];
        case methods.SERVER_EVENT_FILE_TRANSFER_FILE_AVAILABLE:
            return [
                {
                    type: actionTypes.FILE_TRANSFER_FILE_AVAILABLE,
                    payload: {
                        filesRemaining: response.data.files_remaining
                    }
                }
            ];
        case methods.SERVER_EVENT_FILE_TRANSFER_CUSTOMER:
        case methods.SERVER_EVENT_FILE_TRANSFER_CUSTOM_FORMULA:
        case methods.SERVER_EVENT_FILE_TRANSFER_HISTORY:
        case methods.SERVER_EVENT_FILE_TRANSFER_ORDER:
            if (response.data.data.perc_finished === null) {
                return [];
            }
            return [
                {
                    type: actionTypes.FILE_TRANSFER_PROCEEDED,
                    payload: {
                        percFinished: response.data.data.perc_finished
                    }
                }
            ];

        case methods.SERVER_EVENT_FILE_TRANSFER_FINISHED:
            if (!response.data.notification) {
                return [];
            }
            let messageObject = {};
            let resultArray = [];
            resultArray.push({
                type: actionTypes.FILE_TRANSFER_FINISHED
            });
            switch (response.data.kind) {
                case settings.FILE_TRANSFER_KIND_CUSTOMER:
                    messageObject = {
                        errorMessage: "flashMessage.fileTransferCustomerRecordsNotFullyProcessed",
                        infoMessage: "flashMessage.fileTransferCustomerRecordsAdded",
                        dialog: {
                            type: componentTypes.FILE_TRANSFER_DIALOG_CUSTOMER,
                            message: "flashMessage.fileTransferCustomerRecordsProcessed"
                        }
                    };
                    break;

                case settings.FILE_TRANSFER_KIND_HISTORY:
                    messageObject = {
                        errorMessage: "flashMessage.fileTransferCustomFormulaRecordsNotFullyProcessed",
                        infoMessage: "flashMessage.fileTransferHistoryRecordsAdded",
                        dialog: {
                            type: componentTypes.FILE_TRANSFER_DIALOG_HISTORY,
                            message: "flashMessage.fileTransferHistoryRecordsProcessed"
                        }
                    };
                    break;

                case settings.FILE_TRANSFER_KIND_CUSTOM_FORMULA:
                    messageObject = {
                        errorMessage: "flashMessage.fileTransferHistoryRecordsNotFullyProcessed",
                        infoMessage: "flashMessage.fileTransferCustomFormulaRecordsAdded",
                        dialog: {
                            type: componentTypes.FILE_TRANSFER_DIALOG_CUSTOM_FORMULA,
                            message: "flashMessage.fileTransferCustomFormulaRecordsProcessed"
                        }
                    };
                    break;

                //for settings.FILE_TRANSFER_KIND_ORDER, just the prevention from unexpected error
                default:
                    messageObject = {
                        errorMessage: "flashMessage.fileTransferOrderRecordsNotFullyProcessed",
                        infoMessage: "flashMessage.fileTransferOrderRecordsAdded",
                        dialog: {
                            type: componentTypes.FILE_TRANSFER_DIALOG_ORDER,
                            message: "flashMessage.fileTransferOrderRecordsProcessed"
                        }
                    };
            }
            if (response.data.data.errors > 0) {
                resultArray.push(
                    messageHelper.getErrorMessageAction(messageObject.errorMessage, [response.data.data.file])
                );
            }
            if (response.data.data.processed !== 0) {
                resultArray.push(messageHelper.getInfoMessageAction(messageObject.infoMessage));
            }
            if (response.data.notification === settings.NOTIFICATION_DIALOG) {
                resultArray.push({
                    type: actionTypes.OPEN_MESSAGE_DIALOG,
                    payload: {
                        dialog: modelHelper.createMessageDialog(
                            messageObject.dialog.type,
                            translate(messageObject.dialog.message, [
                                response.data.data.processed,
                                response.data.data.processed + response.data.data.errors
                            ]) +
                                (response.data.data.errors > 0
                                    ? " " + translate("flashMessage.wrongDataSaved", ["file_transfer_in/errors"])
                                    : " "),
                            translate("fileTransfer.fileTransferNotification"),
                            null
                        )
                    }
                });
            }
            return resultArray;
        case methods.SERVER_EVENT_FILE_TRANSFER_FORMAT:
            return [messageHelper.getFileTransferError(response)];
        case methods.SERVER_EVENT_FILE_TRANSFER_FORMAT_IN:
            return [messageHelper.getFileTransferInError(response)];
        case methods.SERVER_EVENT_FILE_TRANSFER_FORMAT_OUT:
            return [messageHelper.getFileTransferOutError(response)];
        case methods.SERVER_EVENT_PARTIAL_UPDATE_APPLIED:
            return [
                messageHelper.getSuccessMessageAction("partialUpdates.updatesApplied"),
                {type: actionTypes.GLOBAL_STORE_RELOAD}
            ];
        case methods.SERVER_EVENT_PARTIAL_UPDATE_PREPARING:
            return [partialUpdatesActions.setPartialUpdatesPreparing(response.data.value, response.data.databaseId)];
        case methods.SERVER_EVENT_PARTIAL_UPDATE_PREPARED:
            return [
                partialUpdatesActions.getNewPartialUpdates(),
                {
                    type: actionTypes.MAIN_TAB_RELOAD_AFTER_PARTIAL_UPDATE
                }
            ];
        case methods.SERVER_EVENT_TICKET_ANSWERED:
            return [
                ticketingActions.getTickets(),
                messageHelper.getInfoMessageAction("ticketing.ticketAnswered"),
                {type: actionTypes.TICKETS_INCREASE_NEW_NOTIFICATION_COUNT}
            ];
        case methods.SERVER_EVENT_TICKET_DELIVERED:
            return [ticketingActions.getTickets(), messageHelper.getSuccessMessageAction("ticketing.ticketDelivered")];
        default:
            return [
                {
                    type: actionTypes.UNKNOWN_ACTION
                }
            ];
    }
};
